import React, { useState, useEffect, useRef } from 'react';
import './AIChat.css';
import { userService, conversationsService, messagesService } from '../../services';

export default function GroupChat({ onClose, onBack, conversationId }) {
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [conversationName, setConversationName] = useState('Group Chat');
  const messagesEndRef = useRef(null);
  const currentUser = JSON.parse(localStorage.getItem('user') || 'null');

  // Load conversation details and messages
  useEffect(() => {
    const loadConversation = async () => {
      if (!conversationId || conversationId === 'group') {
        setConversationName('Nhóm');
        return;
      }

      try {
        setLoading(true);
        // Load conversation details
        const conv = await conversationsService.getById(conversationId);
        if (conv) {
          setConversationName(conv.name || conv.title || 'Cuộc trò chuyện');
        }

        // Load messages
        const msgs = await messagesService.getByConversation(conversationId);
        const messageList = Array.isArray(msgs) ? msgs : (msgs?.items || []);
        
        // Map messages to display format
        const mappedMessages = messageList.map(msg => ({
          id: msg.id || msg.messageId,
          content: msg.content || msg.text || '',
          senderId: msg.senderId || msg.sender_id,
          senderName: msg.senderName || 'User',
          timestamp: msg.createdAt || msg.created_at || new Date().toISOString(),
          isOwn: (msg.senderId || msg.sender_id) === currentUser?.id
        }));

        setMessages(mappedMessages);
      } catch (err) {
        console.error('Failed to load conversation', err);
      } finally {
        setLoading(false);
      }
    };

    loadConversation();
  }, [conversationId]);

  // Scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleAddUser = async () => {
    const username = window.prompt('Nhập username để thêm vào cuộc trò chuyện:');
    if (!username) return;

    try {
      // Try to locate the user by username
      const results = await userService.searchUsers(username, 1, 5);
      const matched = Array.isArray(results) 
        ? results.find(u => (u.userName || u.username || u.email || '').toLowerCase() === username.toLowerCase())
        : null;
      
      if (!matched) {
        alert('Không tìm thấy người dùng. Vui lòng kiểm tra lại username.');
        return;
      }

      const userIdToAdd = matched.id || matched.userId || matched._id;
      if (!userIdToAdd) {
        alert('Người dùng không có ID — vui lòng kiểm tra backend.');
        return;
      }

      if (!conversationId || conversationId === 'group') {
        alert('Không thể thêm người vào nhóm mặc định.');
        return;
      }

      // Call the add-member endpoint
      await conversationsService.addMember(conversationId, { userId: userIdToAdd });
      alert('Đã thêm người dùng vào cuộc trò chuyện.');
    } catch (err) {
      console.error('Add user error', err);
      alert(err?.response?.data?.message || err?.message || 'Không thể thêm người dùng');
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!inputMessage.trim()) return;

    if (!conversationId || conversationId === 'group') {
      alert('Không thể gửi tin nhắn vào nhóm mặc định. Vui lòng tạo cuộc trò chuyện mới.');
      return;
    }

    try {
      const newMessage = {
        conversationId: conversationId,
        content: inputMessage.trim(),
        type: 'text'
      };

      const sentMessage = await messagesService.create(newMessage);
      
      // Add message to local state
      const messageToAdd = {
        id: sentMessage.id || sentMessage.messageId,
        content: sentMessage.content || inputMessage.trim(),
        senderId: currentUser?.id,
        senderName: currentUser?.username || currentUser?.userName || 'Bạn',
        timestamp: sentMessage.createdAt || new Date().toISOString(),
        isOwn: true
      };

      setMessages(prev => [...prev, messageToAdd]);
      setInputMessage('');
    } catch (err) {
      console.error('Failed to send message', err);
      alert('Không thể gửi tin nhắn. Vui lòng thử lại.');
    }
  };

  return (
    <div className="ai-chat-root">
      <div className="chat-header">
        <div className="chat-header-left">
          {onBack && (
            <button className="back-btn" onClick={onBack} style={{ marginRight: '10px', background: 'none', border: 'none', cursor: 'pointer', fontSize: '1.2rem' }}>
              ←
            </button>
          )}
          <div>
            <h3 className="selected-chat-name">{conversationName}</h3>
            <div className="selected-chat-status">
              {conversationId && conversationId !== 'group' ? 'Cuộc trò chuyện' : 'Nhóm công khai'}
            </div>
          </div>
        </div>
        <div className="chat-header-right">
          {conversationId && conversationId !== 'group' && (
            <button className="add-user-btn" onClick={handleAddUser}>Thêm người</button>
          )}
        </div>
      </div>

      <div className="chat-messages" style={{ background: 'linear-gradient(180deg,#7b61ff44,#a677d944)' }}>
        {loading ? (
          <div style={{ padding: '20px', textAlign: 'center', color: '#666' }}>Đang tải tin nhắn...</div>
        ) : messages.length === 0 ? (
          <div style={{ padding: '20px', textAlign: 'center', color: '#666' }}>
            {conversationId && conversationId !== 'group' 
              ? 'Chưa có tin nhắn nào. Hãy bắt đầu cuộc trò chuyện!' 
              : 'Chào mừng đến với nhóm chat. Đây là nhóm mặc định.'}
          </div>
        ) : (
          messages.map((msg) => (
            <div key={msg.id} className={`message ${msg.isOwn ? 'user' : 'ai'}`}>
              <div className="message-content">
                <div className={`avatar ${msg.isOwn ? 'user-avatar' : 'ai-avatar'}`}>
                  {msg.isOwn ? 'Bạn' : (msg.senderName?.[0] || 'U')}
                </div>
                <div className={`message-bubble ${msg.isOwn ? 'user-bubble' : 'ai-bubble'}`}>
                  <p>{msg.content}</p>
                  <span className="message-time" style={{ fontSize: '0.75rem', color: '#999', marginTop: '4px', display: 'block' }}>
                    {new Date(msg.timestamp).toLocaleTimeString('vi-VN', { hour: '2-digit', minute: '2-digit' })}
                  </span>
                </div>
              </div>
            </div>
          ))
        )}
        <div ref={messagesEndRef} />
      </div>

      <form className="chat-input" onSubmit={handleSendMessage}>
        <div className="input-form">
          <input 
            className="message-input" 
            placeholder={conversationId && conversationId !== 'group' ? "Nhập tin nhắn..." : "Nhập tin nhắn nhóm..."}
            value={inputMessage}
            onChange={(e) => setInputMessage(e.target.value)}
            disabled={!conversationId || conversationId === 'group'}
          />
        </div>
        <button type="submit" className="send-btn" disabled={!conversationId || conversationId === 'group'}>
          Gửi
        </button>
      </form>
    </div>
  );
}
